import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import { useAuth } from '../../../shared/hooks/useAuth';
import { useApiError } from '../../../shared/errors/useApiError';
import { useNotification } from '../../../shared/notifications/NotificationProvider';
import { useOpportunityMutations } from '../useOpportunities';
import { avatarColor, initials } from '../postVisuals';

interface Props {
  postId: string;
  /** Called by the parent when the visitor isn't signed in — typically
   *  bounces to /login with a return path. */
  onAnonymous: () => void;
}

const COMMENT_MAX = 2000;

/**
 * Input row at the top of a comment thread. Anonymous visitors see a
 * disabled-looking textarea that bounces them to /login on click; signed-in
 * users get a real composer.
 */
export function CommentInput({ postId, onAnonymous }: Props) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { createComment } = useOpportunityMutations();

  const [body, setBody] = useState('');

  const canSubmit = body.trim().length > 0
    && body.length <= COMMENT_MAX
    && !createComment.isPending;

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      onAnonymous();
      return;
    }
    if (!canSubmit) return;
    try {
      await createComment.mutateAsync({ postId, body: { body: body.trim() } });
      setBody('');
    } catch (err) {
      notify.error(translate(err));
    }
  };

  const handleFocus = () => {
    if (!isAuthenticated) onAnonymous();
  };

  const displayName = user?.username
    || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
    || t('opportunities.comments.you');

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: avatarColor(user?.id ?? 'anonymous'),
          color: 'common.white',
          fontSize: 13,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initials(displayName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onFocus={handleFocus}
          placeholder={isAuthenticated
            ? t('opportunities.comments.placeholder')
            : t('opportunities.comments.signInToComment')}
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          disabled={createComment.isPending}
          inputProps={{ maxLength: COMMENT_MAX, 'aria-label': t('opportunities.comments.input') }}
          helperText={`${body.length} / ${COMMENT_MAX}`}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={isAuthenticated && !canSubmit}
            startIcon={
              createComment.isPending
                ? <CircularProgress size={14} color="inherit" />
                : null
            }
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            {createComment.isPending
              ? t('opportunities.comments.posting')
              : t('opportunities.comments.post')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
